import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { bootManifest, normalizeScopePath } from "@/lib/pwa";
import { appScope, rememberLaunchPath } from "@/lib/tenantPath";

rememberLaunchPath();

// Previews of the client page run in an iframe inside the panels: an embedded window must never touch
// the manifest or the service worker registrations of the app that hosts it.
const embedded = window.self !== window.top;
if (!embedded) {
  normalizeScopePath();
  bootManifest();
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 60_000, refetchOnWindowFocus: false } },
});

window.__bfInstall = null;
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  window.__bfInstall = e;
  window.dispatchEvent(new Event("bf-install-ready"));
});

// A waiting worker means a new version is installed but not live yet: the UI offers "Atualizar".
window.__bfUpdate = null;
function watchUpdates(reg) {
  const announce = () => {
    if (reg.waiting && navigator.serviceWorker.controller) {
      window.__bfUpdate = reg;
      window.dispatchEvent(new Event("bf-update-ready"));
    }
  };
  announce();
  reg.addEventListener("updatefound", () => {
    const sw = reg.installing;
    if (sw) sw.addEventListener("statechange", announce);
  });
  const poll = () => reg.update().catch(() => {});
  setInterval(poll, 30 * 60 * 1000);
  document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && poll());
}

// One service worker per app scope. A registration with scope "/" would let the installed app of one
// business answer for the pages of every other business, so legacy global registrations are removed.
if ("serviceWorker" in navigator && !embedded) {
  const own = appScope();
  window.addEventListener("load", async () => {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs
        .filter((r) => {
          const scope = new URL(r.scope).pathname;
          return scope === "/" || (own && !own.scope.startsWith(scope));
        })
        .map((r) => r.unregister()));
      if (own) {
        const reg = await navigator.serviceWorker.register("/service-worker.js", { scope: own.scope });
        watchUpdates(reg);
        navigator.serviceWorker.addEventListener("controllerchange", () => {
          if (window.__bfReloading) window.location.reload();
        });
      }
    } catch {
      /* offline support is optional */
    }
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
